import { useEffect, useMemo, useState } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

const PUBLIC_CMS_URL =
  process.env.REACT_APP_PUBLIC_CMS_URL ||
  'https://system.michaelhemmingsen.com/cms';

const HEADERS = {
  apikey: SUPABASE_KEY,
  Authorization: 'Bearer ' + SUPABASE_KEY
};

const JSON_HEADERS = {
  ...HEADERS,
  'Content-Type': 'application/json'
};

function normalizeInstitutionName(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function displayDate(value) {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString();
}

async function fetchApplications() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_applications?select=*,institutions(id,name)&order=created_at.desc`,
    { headers: HEADERS }
  );

  if (!response.ok) {
    throw new Error(
      `Could not load applications (${response.status}).`
    );
  }

  return response.json();
}

async function fetchInstitutions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/institutions?select=id,name&order=name.asc`,
    { headers: HEADERS }
  );

  if (!response.ok) {
    throw new Error(
      `Could not load institutions (${response.status}).`
    );
  }

  return response.json();
}

async function fetchExistingInstructor(email) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?email=eq.${encodeURIComponent(email)}&select=id,name,email&limit=1`,
    { headers: HEADERS }
  );

  if (!response.ok) {
    throw new Error(
      `Could not check instructor accounts (${response.status}).`
    );
  }

  const rows = await response.json();

  return Array.isArray(rows) && rows.length > 0
    ? rows[0]
    : null;
}

async function patchApplication(
  applicationId,
  data
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_applications?id=eq.${applicationId}`,
    {
      method: 'PATCH',
      headers: {
        ...JSON_HEADERS,
        Prefer: 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Saving application failed (${response.status}): ${detail}`
    );
  }

  return response.json();
}

async function deleteApplication(applicationId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_applications?id=eq.${applicationId}`,
    {
      method: 'DELETE',
      headers: HEADERS
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Removing application failed (${response.status}): ${detail}`
    );
  }
}

async function createInstitution(name) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/institutions`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        Prefer: 'return=representation'
      },
      body: JSON.stringify({ name })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Creating institution failed (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();

  if (!rows?.[0]?.id) {
    throw new Error(
      'The new institution was created without returning an ID.'
    );
  }

  return rows[0];
}

async function createInstructor(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        Prefer: 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Creating instructor failed (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();

  if (!rows?.[0]?.id) {
    throw new Error(
      'The instructor account was created without returning an ID.'
    );
  }

  return rows[0];
}

async function sendApprovalEmail(application) {
  if (!API_URL) {
    throw new Error(
      'REACT_APP_API_URL is not configured.'
    );
  }

  await fetch(API_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      action: 'sendInstructorApproval',
      email: application.email,
      name: application.name,
      loginUrl: PUBLIC_CMS_URL
    })
  });
}

export default function Applications({ session }) {
  const [applications, setApplications] =
    useState([]);
  const [institutions, setInstitutions] =
    useState([]);
  const [selectedId, setSelectedId] =
    useState(null);
  const [institutionDraft, setInstitutionDraft] =
    useState('');

  const [loading, setLoading] = useState(true);
  const [savingInstitution, setSavingInstitution] =
    useState(false);
  const [processing, setProcessing] =
    useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');

    Promise.all([
      fetchApplications(),
      fetchInstitutions()
    ])
      .then(([applicationRows, institutionRows]) => {
        if (cancelled) return;

        const nextApplications =
          Array.isArray(applicationRows)
            ? applicationRows
            : [];

        setApplications(nextApplications);
        setInstitutions(
          Array.isArray(institutionRows)
            ? institutionRows
            : []
        );

        if (
          nextApplications.length > 0
        ) {
          setSelectedId(
            current =>
              current &&
              nextApplications.some(
                application =>
                  application.id === current
              )
                ? current
                : nextApplications[0].id
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error(
            'Applications load failed:',
            err
          );

          setError(
            err?.message ||
              'Could not load applications.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedApplication = useMemo(
    () =>
      applications.find(
        application =>
          application.id === selectedId
      ) || null,
    [applications, selectedId]
  );

  useEffect(() => {
    if (!selectedApplication) {
      setInstitutionDraft('');
      return;
    }

    setInstitutionDraft(
      selectedApplication.institutions?.name ||
        selectedApplication.institution_name ||
        ''
    );
  }, [selectedApplication]);

  const exactInstitution = useMemo(() => {
    const normalized =
      normalizeInstitutionName(institutionDraft);

    if (!normalized) return null;

    return (
      institutions.find(
        institution =>
          normalizeInstitutionName(
            institution.name
          ) === normalized
      ) || null
    );
  }, [institutionDraft, institutions]);

  if (!session?.isSuperadmin) {
    return null;
  }

  async function refreshApplications(
    preferredSelectedId = selectedId
  ) {
    const rows = await fetchApplications();
    const next = Array.isArray(rows) ? rows : [];

    setApplications(next);

    if (
      preferredSelectedId &&
      next.some(
        application =>
          application.id ===
          preferredSelectedId
      )
    ) {
      setSelectedId(preferredSelectedId);
    } else {
      setSelectedId(next[0]?.id || null);
    }
  }

  async function handleSaveInstitution() {
    if (!selectedApplication) return;

    const cleanName =
      String(institutionDraft || '')
        .trim()
        .replace(/\s+/g, ' ');

    if (!cleanName) {
      setError(
        'Institution cannot be blank.'
      );
      return;
    }

    setSavingInstitution(true);
    setError('');
    setSuccess('');

    try {
      const rows = await patchApplication(
        selectedApplication.id,
        {
          institution_id:
            exactInstitution?.id || null,
          institution_name:
            exactInstitution?.name ||
            cleanName
        }
      );

      const updated = rows?.[0];

      if (updated) {
        setApplications(current =>
          current.map(application =>
            application.id ===
            selectedApplication.id
              ? {
                  ...application,
                  ...updated,
                  institutions:
                    exactInstitution
                      ? {
                          id: exactInstitution.id,
                          name: exactInstitution.name
                        }
                      : null
                }
              : application
          )
        );
      } else {
        await refreshApplications(
          selectedApplication.id
        );
      }

      setInstitutionDraft(
        exactInstitution?.name || cleanName
      );
      setSuccess('Institution saved.');
    } catch (err) {
      console.error(
        'Institution save failed:',
        err
      );

      setError(
        err?.message ||
          'Could not save the institution.'
      );
    } finally {
      setSavingInstitution(false);
    }
  }

  async function resolveInstitution() {
    const cleanName =
      String(institutionDraft || '')
        .trim()
        .replace(/\s+/g, ' ');

    if (!cleanName) {
      throw new Error(
        'Institution cannot be blank.'
      );
    }

    const existing =
      institutions.find(
        institution =>
          normalizeInstitutionName(
            institution.name
          ) ===
          normalizeInstitutionName(
            cleanName
          )
      );

    if (existing) {
      return existing;
    }

    const created =
      await createInstitution(cleanName);

    setInstitutions(current =>
      [...current, created].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    );

    return created;
  }

  async function handleAccept() {
    if (!selectedApplication) return;

    const confirmed = window.confirm(
      `Approve ${selectedApplication.name} and create an instructor account?`
    );

    if (!confirmed) return;

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const existingInstructor =
        await fetchExistingInstructor(
          selectedApplication.email
        );

      if (existingInstructor) {
        throw new Error(
          'An instructor account already exists for this email address.'
        );
      }

      const institution =
        await resolveInstitution();

      await createInstructor({
        name: selectedApplication.name.trim(),
        email:
          selectedApplication.email
            .trim()
            .toLowerCase(),
        institution_id: institution.id,
        position:
          selectedApplication.position.trim(),
        is_superadmin: false
      });

      await sendApprovalEmail(
        selectedApplication
      );

      await deleteApplication(
        selectedApplication.id
      );

      const approvedName =
        selectedApplication.name;

      await refreshApplications(null);

      setSuccess(
        `${approvedName} was approved and added as an instructor.`
      );
    } catch (err) {
      console.error(
        'Application approval failed:',
        err
      );

      setError(
        err?.message ||
          'Could not approve this application.'
      );
    } finally {
      setProcessing(false);
    }
  }

  async function handleReject() {
    if (!selectedApplication) return;

    const confirmed = window.confirm(
      `Reject and remove the application from ${selectedApplication.name}?`
    );

    if (!confirmed) return;

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const rejectedName =
        selectedApplication.name;

      await deleteApplication(
        selectedApplication.id
      );

      await refreshApplications(null);

      setSuccess(
        `${rejectedName}'s application was removed.`
      );
    } catch (err) {
      console.error(
        'Application rejection failed:',
        err
      );

      setError(
        err?.message ||
          'Could not remove this application.'
      );
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.muted}>
        Loading applications...
      </div>
    );
  }

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.heading}>
            Applications
          </h2>
          <p style={styles.muted}>
            Review applications for instructor accounts.
          </p>
        </div>

        <div style={styles.countPill}>
          {applications.length}{' '}
          {applications.length === 1
            ? 'application'
            : 'applications'}
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {success && (
        <div style={styles.success}>
          {success}
        </div>
      )}

      {applications.length === 0 ? (
        <div style={styles.empty}>
          No pending instructor applications.
        </div>
      ) : (
        <div style={styles.layout}>
          <div style={styles.list}>
            {applications.map(application => {
              const selected =
                application.id === selectedId;

              return (
                <button
                  key={application.id}
                  type="button"
                  style={{
                    ...styles.listItem,
                    ...(selected
                      ? styles.listItemSelected
                      : {})
                  }}
                  onClick={() => {
                    setSelectedId(
                      application.id
                    );
                    setError('');
                    setSuccess('');
                  }}
                >
                  <div style={styles.listName}>
                    {application.name}
                  </div>

                  <div style={styles.listEmail}>
                    {application.email}
                  </div>

                  <div style={styles.listInstitution}>
                    {application.institutions?.name ||
                      application.institution_name}
                  </div>

                  <div style={styles.listDate}>
                    {displayDate(
                      application.created_at
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedApplication && (
            <div style={styles.detailCard}>
              <h3 style={styles.detailName}>
                {selectedApplication.name}
              </h3>

              <a
                href={`mailto:${selectedApplication.email}`}
                style={styles.emailLink}
              >
                {selectedApplication.email}
              </a>

              <div style={styles.detailField}>
                <div style={styles.label}>
                  Institution
                </div>

                <div style={styles.institutionRow}>
                  <input
                    style={styles.input}
                    type="text"
                    list="application-institutions"
                    value={institutionDraft}
                    onChange={event =>
                      setInstitutionDraft(
                        event.target.value
                      )
                    }
                  />

                  <datalist id="application-institutions">
                    {institutions.map(
                      institution => (
                        <option
                          key={institution.id}
                          value={institution.name}
                        />
                      )
                    )}
                  </datalist>

                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    disabled={
                      savingInstitution ||
                      processing
                    }
                    onClick={
                      handleSaveInstitution
                    }
                  >
                    {savingInstitution
                      ? 'Saving...'
                      : 'Save Institution'}
                  </button>
                </div>

                <div style={styles.help}>
                  {exactInstitution
                    ? 'This matches an institution already on file.'
                    : 'If this is a new institution, it will be created when the application is accepted.'}
                </div>
              </div>

              <div style={styles.detailField}>
                <div style={styles.label}>
                  Position
                </div>
                <div style={styles.valueBox}>
                  {selectedApplication.position}
                </div>
              </div>

              <div style={styles.detailField}>
                <div style={styles.label}>
                  Tell us a little about how you’d like
                  to use the Instructor System
                </div>
                <div style={styles.longValue}>
                  {selectedApplication.use_description}
                </div>
              </div>

              <div style={styles.meta}>
                Submitted{' '}
                {displayDate(
                  selectedApplication.created_at
                )}
              </div>

              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.rejectBtn}
                  disabled={processing}
                  onClick={handleReject}
                >
                  Reject
                </button>

                <button
                  type="button"
                  style={styles.acceptBtn}
                  disabled={processing}
                  onClick={handleAccept}
                >
                  {processing
                    ? 'Processing...'
                    : 'Accept'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px'
  },
  heading: {
    margin: '0 0 6px',
    fontSize: '22px',
    color: '#1f2933'
  },
  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: 0
  },
  countPill: {
    background: '#e7f1f7',
    color: '#215b7d',
    borderRadius: '999px',
    padding: '5px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },
  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed',
    border: '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '14px',
    marginBottom: '16px'
  },
  empty: {
    background: 'white',
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '24px',
    color: '#66788a',
    fontSize: '14px'
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    gap: '18px',
    alignItems: 'start'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  listItem: {
    width: '100%',
    textAlign: 'left',
    border: '1px solid #d7dee5',
    background: 'white',
    borderRadius: '9px',
    padding: '12px 14px',
    cursor: 'pointer'
  },
  listItemSelected: {
    borderColor: '#8eb7cd',
    background: '#eef6fa'
  },
  listName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1f2933',
    marginBottom: '2px'
  },
  listEmail: {
    fontSize: '12px',
    color: '#66788a',
    marginBottom: '5px'
  },
  listInstitution: {
    fontSize: '13px',
    color: '#42515f',
    marginBottom: '5px'
  },
  listDate: {
    fontSize: '11px',
    color: '#8a98a6'
  },
  detailCard: {
    background: 'white',
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '20px'
  },
  detailName: {
    margin: '0 0 4px',
    fontSize: '20px',
    color: '#1f2933'
  },
  emailLink: {
    display: 'inline-block',
    color: '#215b7d',
    fontSize: '14px',
    marginBottom: '22px'
  },
  detailField: {
    marginBottom: '18px'
  },
  label: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '6px'
  },
  institutionRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  input: {
    flex: 1,
    minWidth: 0,
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  help: {
    color: '#66788a',
    fontSize: '12px',
    lineHeight: '1.4',
    marginTop: '6px'
  },
  valueBox: {
    border: '1px solid #e1e6eb',
    borderRadius: '8px',
    padding: '10px 12px',
    color: '#1f2933',
    fontSize: '14px',
    background: '#f8fafb'
  },
  longValue: {
    border: '1px solid #e1e6eb',
    borderRadius: '8px',
    padding: '12px',
    color: '#1f2933',
    fontSize: '14px',
    lineHeight: '1.55',
    whiteSpace: 'pre-wrap',
    background: '#f8fafb'
  },
  meta: {
    color: '#8a98a6',
    fontSize: '12px',
    marginBottom: '18px'
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    borderTop: '1px solid #e1e6eb',
    paddingTop: '16px'
  },
  secondaryBtn: {
    background: 'white',
    color: '#42515f',
    border: '1px solid #cbd5dd',
    borderRadius: '8px',
    padding: '8px 11px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  rejectBtn: {
    background: 'white',
    color: '#a12a2a',
    border: '1px solid #e2b8b8',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  acceptBtn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 18px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};